import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  MapPin,
  DollarSign,
  Clock,
  TreePine,
  Users,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Target,
  Hammer,
  Eye,
  Shield,
  FileText,
  Camera,
  CreditCard,
  Loader2,
  ChevronRight,
  Award,
  User,
  Hash
} from 'lucide-react';

const API = `${import.meta.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const statusConfig = {
  available: { color: 'bg-slate-500', textColor: 'text-slate-600', label: 'Awaiting Funding', icon: Target },
  funded: { color: 'bg-green-500', textColor: 'text-green-600', label: 'Fully Funded', icon: DollarSign },
  claimed: { color: 'bg-blue-500', textColor: 'text-blue-600', label: 'Work In Progress', icon: Hammer },
  submitted: { color: 'bg-amber-500', textColor: 'text-amber-600', label: 'Proof Submitted', icon: Eye },
  verified: { color: 'bg-emerald-500', textColor: 'text-emerald-600', label: 'Mission Complete', icon: CheckCircle2 },
  rejected: { color: 'bg-red-500', textColor: 'text-red-600', label: 'Rejected', icon: AlertCircle }
};

export default function ZoneDetailPage() {
  const { zoneId } = useParams();
  const navigate = useNavigate();
  const [zone, setZone] = useState(null);
  const [donations, setDonations] = useState([]);
  const [currentClaim, setCurrentClaim] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  // Donation form state
  const [donationAmount, setDonationAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch zone data
  useEffect(() => {
    const fetchZone = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/marketplace/zones/${zoneId}`);
        if (!res.ok) throw new Error('Zone not found');
        const data = await res.json();
        setZone(data.zone);
        setDonations(data.donations || []);
        setCurrentClaim(data.current_claim);
        setSubmission(data.submission);
      } catch (err) {
        console.error('Failed to fetch zone:', err);
        toast.error('Failed to load mission');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    fetchZone();
  }, [zoneId, navigate]);

  // Handle donation
  const handleDonate = async (e) => {
    e.preventDefault();
    const amount = parseFloat(donationAmount);
    
    if (!amount || amount < 1) {
      toast.error('Minimum donation is $1');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/marketplace/zones/${zoneId}/fund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          donor_name: donorName || 'Anonymous',
          donor_email: donorEmail || null,
          is_public: isPublic,
          direct_donation: true // For MVP - bypasses Stripe
        })
      });

      if (!res.ok) throw new Error('Failed to process donation');
      
      const data = await res.json();
      toast.success(`Thank you! $${amount} donated successfully`);

      // Refresh zone data
      const zoneRes = await fetch(`${API}/marketplace/zones/${zoneId}`);
      const zoneData = await zoneRes.json();
      setZone(zoneData.zone);
      setDonations(zoneData.donations || []);

      // Clear form
      setDonationAmount('');
      setDonorName('');
      setDonorEmail('');
      
    } catch (err) {
      toast.error('Failed to process donation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!zone) return null;

  const status = statusConfig[zone.status] || statusConfig.available;
  const StatusIcon = status.icon;
  const fundingProgress = zone.funding_progress || 0;
  const amountNeeded = Math.max(0, (zone.bounty_target_amount || 0) - (zone.funded_amount || 0));

  return (
    <div className="min-h-screen bg-background" data-testid="zone-detail-page">
      <Navbar />

      {/* Header */}
      <section className="pt-20 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          <Link to="/marketplace" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Marketplace
          </Link>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={`${status.color} text-white`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
                <Badge variant="outline">
                  {zone.method === 'manual' ? 'Manual De-Pining' : 'Mechanical'}
                </Badge>
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{zone.name}</h1>
              <p className="text-muted-foreground max-w-2xl">{zone.description}</p>
            </div>

            <div className="flex flex-col items-end gap-2">
              <div className="text-3xl font-bold text-primary">
                ${zone.bounty_target_amount?.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">Mission Bounty</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="instructions">Instructions</TabsTrigger>
                  <TabsTrigger value="proof">Proof</TabsTrigger>
                </TabsList>

                {/* Details Tab */}
                <TabsContent value="details" className="space-y-6">
                  {/* Stats Grid */}
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <div className="text-center">
                          <TreePine className="w-8 h-8 mx-auto text-green-600 mb-2" />
                          <div className="text-2xl font-bold">{zone.hectares || '?'}</div>
                          <div className="text-sm text-muted-foreground">Hectares</div>
                        </div>
                        <div className="text-center">
                          <Clock className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                          <div className="text-2xl font-bold">{zone.estimated_hours || '?'}</div>
                          <div className="text-sm text-muted-foreground">Est. Hours</div>
                        </div>
                        <div className="text-center">
                          <Target className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                          <div className="text-2xl font-bold capitalize">{zone.difficulty}</div>
                          <div className="text-sm text-muted-foreground">Difficulty</div>
                        </div>
                        <div className="text-center">
                          <Hammer className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                          <div className="text-2xl font-bold capitalize">{zone.method}</div>
                          <div className="text-sm text-muted-foreground">Method</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Funding Progress */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        Funding Progress
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-2xl font-bold">${zone.funded_amount?.toLocaleString() || 0}</span>
                            <span className="text-muted-foreground">of ${zone.bounty_target_amount?.toLocaleString()}</span>
                          </div>
                          <Progress value={fundingProgress} className="h-3" />
                          <div className="flex justify-between mt-2 text-sm">
                            <span className="text-muted-foreground">{fundingProgress}% funded</span>
                            {amountNeeded > 0 && (
                              <span className="text-primary font-medium">${amountNeeded.toLocaleString()} needed</span>
                            )}
                          </div>
                        </div>

                        {/* Recent Donors */}
                        {donations.length > 0 && (
                          <div className="border-t pt-4">
                            <h4 className="font-medium mb-3">Recent Contributors</h4>
                            <div className="space-y-2">
                              {donations.slice(0, 5).map((d, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                  <span className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    {d.is_public ? d.donor_name : 'Anonymous'}
                                  </span>
                                  <span className="font-medium">${d.amount}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Current Claim Info */}
                  {currentClaim && (
                    <Card className="border-blue-500/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-blue-600">
                          <Hammer className="w-5 h-5" />
                          Mission Claimed
                        </CardTitle>
                        <CardDescription>
                          A worker has claimed this mission and is currently working on it.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Claimed:</span>
                            <div className="font-medium">{new Date(currentClaim.claimed_at).toLocaleDateString()}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Due:</span>
                            <div className="font-medium">{new Date(currentClaim.due_date).toLocaleDateString()}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Status:</span>
                            <div className="font-medium capitalize">{currentClaim.status}</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Instructions Tab */}
                <TabsContent value="instructions" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Work Instructions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <pre className="whitespace-pre-wrap bg-muted p-4 rounded-lg text-sm">
                          {zone.instructions || 'No specific instructions provided.'}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>

                  {zone.safety_notes && (
                    <Card className="border-amber-500/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-amber-600">
                          <Shield className="w-5 h-5" />
                          Safety Notes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{zone.safety_notes}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Proof Tab */}
                <TabsContent value="proof" className="space-y-6">
                  {submission ? (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Camera className="w-5 h-5" />
                          Submitted Proof
                        </CardTitle>
                        <CardDescription>
                          Submitted on {new Date(submission.timestamp).toLocaleString()}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Before Photos */}
                        <div>
                          <h4 className="font-medium mb-2">Before Photos ({submission.before_media?.length || 0})</h4>
                          <div className="grid grid-cols-3 gap-2">
                            {submission.before_media?.map((url, i) => (
                              <img 
                                key={i} 
                                src={`${import.meta.env.REACT_APP_BACKEND_URL}${url}`}
                                alt={`Before ${i + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        </div>

                        {/* After Photos */}
                        <div>
                          <h4 className="font-medium mb-2">After Photos ({submission.after_media?.length || 0})</h4>
                          <div className="grid grid-cols-3 gap-2">
                            {submission.after_media?.map((url, i) => (
                              <img 
                                key={i} 
                                src={`${import.meta.env.REACT_APP_BACKEND_URL}${url}`}
                                alt={`After ${i + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        </div>

                        {/* GPS & Verification */}
                        <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                          <div>
                            <span className="text-muted-foreground">GPS Location:</span>
                            <div className="font-mono text-xs">
                              {submission.gps_lat?.toFixed(6)}, {submission.gps_lng?.toFixed(6)}
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground flex items-center gap-1">
                              <Hash className="w-3 h-3" /> Verification Hash:
                            </span>
                            <div className="font-mono text-xs truncate">
                              {submission.verification_hash}
                            </div>
                          </div>
                        </div>

                        {submission.notes && (
                          <div className="border-t pt-4">
                            <span className="text-muted-foreground">Worker Notes:</span>
                            <p className="text-sm mt-1">{submission.notes}</p>
                          </div>
                        )}

                        {/* Admin Review */}
                        {submission.admin_review && (
                          <div className={`border-t pt-4 ${submission.admin_review.action === 'verify' ? 'text-green-600' : 'text-red-600'}`}>
                            <div className="flex items-center gap-2 font-medium">
                              {submission.admin_review.action === 'verify' ? (
                                <><CheckCircle2 className="w-4 h-4" /> Verified</>
                              ) : (
                                <><AlertCircle className="w-4 h-4" /> Rejected</>
                              )}
                            </div>
                            {submission.admin_review.notes && (
                              <p className="text-sm mt-1 text-foreground">{submission.admin_review.notes}</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Camera className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                        <p className="text-muted-foreground">No proof submitted yet</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
            </div>

            {/* Right Column - Donation Form */}
            <div className="space-y-6">
              {zone.status === 'available' && (
                <Card className="sticky top-24">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-primary" />
                      Fund This Mission
                    </CardTitle>
                    <CardDescription>
                      {amountNeeded > 0 
                        ? `$${amountNeeded.toLocaleString()} needed to fully fund`
                        : 'Mission is fully funded!'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleDonate} className="space-y-4">
                      <div>
                        <Label htmlFor="amount">Amount (USD)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="amount"
                            type="number"
                            min="1"
                            step="1"
                            value={donationAmount}
                            onChange={(e) => setDonationAmount(e.target.value)}
                            placeholder="100"
                            className="pl-10"
                            required
                            data-testid="donation-amount"
                          />
                        </div>
                        {/* Quick amounts */}
                        <div className="flex gap-2 mt-2">
                          {[50, 100, 250, 500].map((amt) => (
                            <Button
                              key={amt}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setDonationAmount(amt.toString())}
                              className="flex-1"
                            >
                              ${amt}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="donorName">Your Name (optional)</Label>
                        <Input
                          id="donorName"
                          value={donorName}
                          onChange={(e) => setDonorName(e.target.value)}
                          placeholder="Anonymous"
                          data-testid="donor-name"
                        />
                      </div>

                      <div>
                        <Label htmlFor="donorEmail">Email (for receipt)</Label>
                        <Input
                          id="donorEmail"
                          type="email"
                          value={donorEmail}
                          onChange={(e) => setDonorEmail(e.target.value)}
                          placeholder="you@example.com"
                          data-testid="donor-email"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="isPublic"
                          checked={isPublic}
                          onChange={(e) => setIsPublic(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="isPublic" className="text-sm font-normal cursor-pointer">
                          Show my name publicly as a contributor
                        </Label>
                      </div>

                      <Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={submitting}
                        data-testid="donate-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4 mr-2" />
                            Donate Now
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-muted-foreground text-center">
                        100% of your donation goes to the mission bounty.
                        Payment released only after verified completion.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              )}

              {zone.status === 'funded' && (
                <Card className="border-green-500/50">
                  <CardContent className="py-8 text-center">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                    <h3 className="font-bold text-lg mb-2">Fully Funded!</h3>
                    <p className="text-muted-foreground text-sm">
                      This mission is ready for a worker to claim.
                    </p>
                    <Link to="/worker">
                      <Button className="mt-4">
                        Go to Worker Portal
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}

              {zone.status === 'verified' && (
                <Card className="border-emerald-500/50">
                  <CardContent className="py-8 text-center">
                    <Award className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
                    <h3 className="font-bold text-lg mb-2">Mission Complete!</h3>
                    <p className="text-muted-foreground text-sm">
                      This mission has been verified and completed.
                      Thank you to all contributors!
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
